import React, { ReactNode, useMemo, useState, useEffect } from "react";
import AddressFields from "../form-fields/AddressFields";
import arrayMutators from "final-form-arrays";
import AutocompleteAddressField from "./AutocompleteAddressField";
import Box from "../boxes/Box";
import Button from "../controls/Button";
import cn from "classnames";
import ContinueButton from "../controls/ContinueButton";
import DependantsField from "./DependantsField";
import Header from "../boxes/Header";
import profileApi from "../../api/profile";
import PoliticallyExposedPersonsFields from "./PoliticallyExposedPersonsFields";
import StyledButtonsArray from "../form-fields/StyledButtonsArray";
import SubmitError from "../form-fields/SubmitError";
import { Address } from "../../types/address";
import { counties, countyOptions, getBorrowerCountryOptions } from "../../data/countries";
import { decamelize } from "humps";
import { deleteField } from "./PoliticallyExposedPersonsFields";
import { emptyPep } from "./PoliticallyExposedPersonsFields";
import { formatAddress } from "../../utils/formats";
import { Form, FormRenderProps } from "react-final-form";
import { EducationLevel, MaritalStatus, PoliticallyExposedPerson, UserProfile } from "../../types/user";
import { educationLevelOptions, maritalStatusesOptions } from "../../utils/user";
import { t } from "../../i18n";
import { NewLoanStepProps } from "../new-loan/types";
import LoaderModal from "../modals/LoaderModal";
import { trackGTM, generateDataLayerEventObject, convertToDataLayerName } from "../../utils/trackGTM";
import { trackGAEventforPHD, generateOnboardingMilestoneEventObject } from "../../utils/trackGAEventforPHD";

interface FormValues extends Address {
  educationLevel: EducationLevel | string;
  maritalStatus: MaritalStatus | string;
  numberOfDependants: null | number;
  politicallyExposedPersons: PoliticallyExposedPerson[];
  politicallyInfluential: boolean;
}

const initialValuesFromUser = (profile: UserProfile): FormValues => {
  return {
    addressCountryCode: profile.addressCountryCode || "",
    apartmentNumber: profile.apartmentNumber || "",
    city: profile.city || "",
    county: profile.county || countyOptions[0].name,
    educationLevel: profile.educationLevel || "",
    houseNumber: profile.houseNumber || "",
    maritalStatus: profile.maritalStatus || "",
    numberOfDependants: profile.numberOfDependants || 0,
    politicallyExposedPersons: profile.politicallyExposedPersons || [emptyPep],
    politicallyInfluential: profile.politicallyInfluential || false,
    postalCode: profile.postalCode || "",
    street: profile.street || "",
  };
};

interface TogglerProrps {
  status: boolean;
  callback: (value: boolean) => void;
}

export const countiesValuesCompare = (value: string): string | undefined => {
  return counties.find((el: string) => el.includes(value));
};

const Toggler = ({ status, callback }: TogglerProrps) => {
  return (
    <div>
      {[
        { label: "yes", value: true },
        { label: "no", value: false },
      ].map((element, index) => (
        <Button
          key={index}
          className={cn("SelectionButton small", { isSelected: status === element.value })}
          onClick={() => callback(element.value)}
        >
          {t(`simple_form.${element.label}`)}
        </Button>
      ))}
    </div>
  );
};

const AdditionalDataForm = ({ loan, profile, onForward }: NewLoanStepProps): JSX.Element => {
  const initialValues = useMemo(() => initialValuesFromUser(profile), []);
  const [isAddressMatches, setIsAddressMatches] = useState(!!profile.residentialAddress);

  const submitForm = async (values: FormValues) => {
    try {
      if (!values.politicallyInfluential && profile.politicallyExposedPersons.length > 0) {

        await Promise.all(profile.politicallyExposedPersons.map(field => {
          if (field.id !== null) return deleteField(field.id)
            .then(response => console.log(response))
            .catch(err => console.log(err));
        }));
      }

      values.politicallyExposedPersons?.forEach(pep => {
        pep.birthPlaceCountryCode = pep.birthPlaceCountryCode === "" ? null : pep.birthPlaceCountryCode;
      });

      const profileUpdateResult = await profileApi.updateProfile(values, onForward);

      if (profileUpdateResult && typeof profileUpdateResult === 'object' && (
        Array.isArray(profileUpdateResult.addressCountryCode) ||
        Array.isArray(profileUpdateResult.city) ||
        Array.isArray(profileUpdateResult.county) ||
        Array.isArray(profileUpdateResult.educationLevel) ||
        Array.isArray(profileUpdateResult.maritalStatus) ||
        Array.isArray(profileUpdateResult.postalCode) ||
        Array.isArray(profileUpdateResult.street)
      )) {
        throw profileUpdateResult;
      }

      trackGTM({
        ...generateDataLayerEventObject('additional_info'),
        item_name: convertToDataLayerName(loan.purpose),
      });

      trackGAEventforPHD(generateOnboardingMilestoneEventObject(profile.mzid, 'additional_data'));

    } catch (error) {
      console.error("Error when updating aditional data", error);

      return error;
    }
  };

  return (
    <main className="PageSection">
      <div className="PageSection-content">
        <Form
          initialValues={initialValues}
          onSubmit={submitForm}
          mutators={{ ...arrayMutators }}
          render={(renderProps: FormRenderProps<FormValues>): ReactNode => {
            const { handleSubmit, form, values, submitting, submitErrors } = renderProps;
            const addressFields = ["addressCountryCode", "city", "county", "houseNumber", "postalCode", "street"];
            const autocompleteErrors = useMemo(() => {
              if (submitErrors) {
                return Object.
                  entries(submitErrors).
                  filter(el => addressFields.includes(el[0])).
                  map(([key, values]: [key: string, values: string[]]) => `${t(`attributes.${decamelize(key)}`)}: ${values.join(",")}`)
              }
            }, [submitErrors]);

            useEffect(() => {
              const registerAddress: Address | null = profile.residentialAddress;
              form.change(
                "city",
                isAddressMatches ? registerAddress?.city : ""
              );
              form.change(
                "county",
                isAddressMatches ? countiesValuesCompare(registerAddress?.county || "") : ""
              );
              form.change(
                "addressCountryCode",
                "EE"
              );
              form.change(
                "street",
                isAddressMatches ? registerAddress?.street : ""
              );
              form.change(
                "apartmentNumber",
                isAddressMatches ? registerAddress?.apartmentNumber : ""
              );
              form.change(
                "houseNumber",
                isAddressMatches ? registerAddress?.houseNumber : ""
              );
              form.change(
                "postalCode",
                isAddressMatches ? registerAddress?.postalCode : ""
              );
            }, [isAddressMatches]);

            useEffect(() => {
              form.change("politicallyExposedPersons", values.politicallyInfluential ?
                (profile.politicallyExposedPersons.length > 0 ? profile.politicallyExposedPersons : [emptyPep]) :
                []
            )}, [values.politicallyInfluential])

            return (
              <form id="form_additional_data" onSubmit={handleSubmit}>
                <Header i18nKey="loan.steps.additional"/>
                <Box maxWidth="580px">
                  {profile.residentialAddress &&
                    <>
                     <div>
                       <p className="mt-10 mb-5 font-semibold">{t("loan.additional_data.address_predict_title")}</p>
                       <p className="mb-2">{formatAddress(profile.residentialAddress) ?? ""}</p>
                     </div>
                     <Toggler status={isAddressMatches} callback={setIsAddressMatches}/>
                   </>}
                   {!isAddressMatches &&
                    <>
                      <p className="mt-10 mb-5 font-semibold">
                        {t("loan.additional_data.address_new_title")}
                      </p>
                      <AutocompleteAddressField/>
                      {(values.city || values.county || values.postalCode) &&
                        <>
                          <p className="mt-10 mb-5 font-semibold">
                            {t("loan.additional_data.address_manual_form_title")}
                          </p>
                          <AddressFields
                            countryOptions={getBorrowerCountryOptions()}
                          />
                        </>
                      }
                    </>}
                  <SubmitError errors={autocompleteErrors!}/>

                  <StyledButtonsArray
                    options={maritalStatusesOptions}
                    i18nScope="profile.marital_status"
                    fieldName="maritalStatus"
                  />
                  <DependantsField numberOfDependants={values.numberOfDependants}/>
                  <StyledButtonsArray
                    options={educationLevelOptions}
                    i18nScope="profile.education_levels"
                    fieldName="educationLevel"
                  />
                  <p className="mt-10 mb-5 font-semibold">
                    {t("loan.additional_data.pep_title")}
                  </p>
                  <div className="mt-5 mb-5" dangerouslySetInnerHTML={{__html: t("loan.additional_data.pep_subtitle")}} />
                  <Toggler
                    status={values.politicallyInfluential}
                    callback={(value) => {
                      form.change("politicallyInfluential", value);
                    }}
                  />
                  {values.politicallyInfluential && <PoliticallyExposedPersonsFields/>}
                  <div>
                    <ContinueButton disabled={submitting} secondary={false} />
                  </div>
                  {submitting && <LoaderModal />}
                </Box>
              </form>
            );
          }}
        />
      </div>
    </main>
  );
};

export default AdditionalDataForm;
